import React from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Typography,
} from "@material-ui/core";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 2),
  },
  content: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  spaceAfter: {
    marginBottom: 15,
  },
  button: {
    marginTop: theme.spacing(3),
  },
}));

// Where the link emailed after the "request a team" form lands
// (CreateTeamRequest in api/views.py). Opening the page only checks the
// link (GET); confirming is a button press (POST), so an email scanner or
// link preview can't confirm an address on someone's behalf. Public - the
// unguessable token is the proof and it may be opened on any device.
export const VerifyTeamRequest = () => {
  const classes = useStyles();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");
  // checking | ready | working | confirmed | invalid
  const [phase, setPhase] = React.useState(token ? "checking" : "invalid");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!token) return;
    axios
      .get(`/api/team/request/verify/${encodeURIComponent(token)}/`)
      .then((res) => setPhase(res.data.already_confirmed ? "confirmed" : "ready"))
      .catch(() => setPhase("invalid"));
  }, [token]);

  const confirm = () => {
    setPhase("working");
    setError("");
    axios
      .post(`/api/team/request/verify/${encodeURIComponent(token)}/`)
      .then(() => setPhase("confirmed"))
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          setPhase("invalid");
          return;
        }
        setError("Something went wrong - try again.");
        setPhase("ready");
      });
  };

  return (
    <Box className={classes.root} component="main">
      <CssBaseline />
      <Container maxWidth="xs">
        <div className={classes.content}>
          <Avatar className={classes.avatar}>
            <MailOutlineIcon />
          </Avatar>

          {phase === "checking" && <CircularProgress color="secondary" />}

          {(phase === "ready" || phase === "working") && (
            <>
              <Typography component="h1" variant="h5" className={classes.spaceAfter}>
                Confirm your email
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Confirming tells us this is your address, and sends your team
                request to us to review.
              </Typography>
              {error && (
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              )}
              <Button
                className={classes.button}
                variant="contained"
                color="secondary"
                onClick={confirm}
                disabled={phase === "working"}
              >
                Confirm my email
              </Button>
            </>
          )}

          {phase === "confirmed" && (
            <>
              <Typography component="h1" variant="h5" className={classes.spaceAfter}>
                Thanks - you're confirmed
              </Typography>
              <Typography variant="body1" color="textSecondary">
                We'll review your request and email you again once it's approved,
                with a link to set up your team.
              </Typography>
              <Button className={classes.button} variant="contained" color="primary" href="/">
                Done
              </Button>
            </>
          )}

          {phase === "invalid" && (
            <>
              <Typography component="h1" variant="h5" className={classes.spaceAfter}>
                This link isn't valid
              </Typography>
              <Typography variant="body1" color="textSecondary">
                It has expired or isn't right. You can send a new request from the
                sign-up page.
              </Typography>
              <Button className={classes.button} variant="contained" color="primary" href="/request-access">
                Request access
              </Button>
            </>
          )}
        </div>
      </Container>
    </Box>
  );
};

export default VerifyTeamRequest;
